import { useState, useMemo } from 'react';
import type { UIMessage } from 'ai';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CheckCircle2, XCircle, AlertCircle, ChevronRight, Square, CircleStop } from 'lucide-react';
import { cn, Button } from '@scio3/ui';
import { safeToLocaleTime } from '@/lib/safe-format';
import { getToolLabel, getToolDoneLabel, isErrorResult, isWriteTool } from '../utils/tool-display';
import { ApprovalCard } from './ApprovalCard';
import { BotAvatar } from './BotAvatar';
import { AgentAvatar } from './AgentAvatar';
import { FeedbackButtons } from './FeedbackButtons';
import { TemplateCards } from './TemplateCards';
import { DesignCards } from './DesignCards';

interface AssistantMessageProps {
  message: UIMessage;
  isLast: boolean;
  isLoading: boolean;
  onApprove?: (toolCallId: string) => void;
  onDeny?: (toolCallId: string) => void;
  onFeedback?: (messageId: string, score: number, comment?: string) => void;
  /** Sent time — rendered on the right of the feedback row below the bubble. */
  timestamp?: Date;
}

/**
 * Left-aligned chat bubble for assistant responses with bot avatar.
 * Renders markdown text parts and tool invocation parts interleaved.
 * Consecutive completed tool steps are collapsed into an expandable summary.
 *
 * Tool invocation states (AI SDK v6):
 * - input-streaming / input-available → 3-dot typing indicator
 * - approval-requested → ApprovalCard (write tools with needsApproval)
 * - output-denied → denied badge (user rejected write tool)
 * - output-available → collapsed step line
 * - output-error → inline error
 */
export function AssistantMessage({
  message,
  isLast,
  isLoading,
  onApprove,
  onDeny,
  onFeedback,
  timestamp,
}: AssistantMessageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isStreaming = isLast && isLoading;

  // Markdown components with internal link interception
  const mdComponents = useMemo(() => createMarkdownComponents(navigate), [navigate]);

  // Group parts: consecutive completed tool invocations get collapsed
  const renderedParts = groupAndRenderParts(message.parts, {
    t,
    onApprove,
    onDeny,
    mdComponents,
    isLoading: isStreaming,
  });

  // Base "has content" on what actually renders in the bubble, not raw part count.
  // When a turn opens, non-rendering parts exist before the first token (an empty
  // text placeholder and/or a step-start marker). They must not count as content,
  // or the bubble shows an empty box instead of the typing indicator — and on Stop
  // feedback buttons would attach to a blank bubble.
  const hasContent = renderedParts.length > 0;

  const isAgentReply = (message.metadata as { isAgentReply?: boolean } | undefined)?.isAgentReply;

  const showFeedback = !isStreaming && hasContent && !!onFeedback && !isStoppedMessage(message);
  // Timestamp is hidden while the message is still streaming (matches the prior behaviour).
  const time = isStreaming
    ? null
    : safeToLocaleTime(timestamp, { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="group/assistant flex items-start gap-2" data-testid="assistant-message">
      {isAgentReply ? <AgentAvatar /> : <BotAvatar />}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        {/* No chat bubble — plain text next to the avatar (the avatar still tells the
            user whether this is the bot or a human agent). pt-1 vertically centers the
            first line against the avatar. */}
        <div className="pt-1 text-sm text-foreground">
          {renderedParts}
          {isStreaming && !hasContent && <TypingIndicator />}
        </div>
        {/* Footer below the bubble: feedback then timestamp, both left-aligned.
            Negative margin pulls the thumb glyphs out to align with the bubble's
            left edge (cancels the icon button's internal padding). */}
        {(showFeedback || time) && (
          <div className="flex items-center gap-2">
            {showFeedback && (
              <div className="-ml-1.5">
                <FeedbackButtons messageId={message.id} onFeedback={onFeedback} />
              </div>
            )}
            {time && <span className="text-[10px] text-muted-foreground">{time}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// --- 3-dot typing indicator (R2) ---

export function TypingIndicator() {
  return (
    // h-5 matches the bubble's text line-height (text-sm = 20px) so swapping the
    // dots for the first line of streamed text doesn't shift the bubble height.
    <div className="flex h-5 items-center gap-1" aria-label="Typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full bg-muted-foreground/50"
          style={{
            animation: 'optibot-dot-bounce 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
}

// --- Part grouping & rendering (R3) ---

interface RenderContext {
  t: (key: string, options?: Record<string, unknown>) => string;
  onApprove?: (id: string) => void;
  onDeny?: (id: string) => void;
  mdComponents: Record<string, React.ComponentType<Record<string, unknown>>>;
  /** When false, in-progress tool parts should render as interrupted (user clicked Stop). */
  isLoading: boolean;
}

interface ToolPartShape {
  type: string;
  /** AI SDK v6: toolName may be a separate field or encoded in type as "tool-{name}" */
  toolName: string;
  toolCallId: string;
  state: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  /** AI SDK v6: approval object present when state is 'approval-requested' */
  approval?: { id: string };
}

function isToolPart(part: { type: string }): part is ToolPartShape {
  return part.type === 'dynamic-tool' || part.type.startsWith('tool-');
}

/** Extract tool name — AI SDK v6 encodes it in the type field as "tool-{name}" */
function getToolName(part: ToolPartShape): string {
  if (part.toolName) return part.toolName;
  if (part.type.startsWith('tool-')) return part.type.slice(5);
  return part.type;
}

/** Is this a "completed read tool" that should be collapsible? */
function isCompletedReadTool(part: ToolPartShape): boolean {
  if (part.state !== 'output-available') return false;
  if (isWriteTool(getToolName(part))) return false;
  // Errors should still show individually
  if (isErrorResult(part.output)) return false;
  return true;
}

/** Check if a message is a "stopped" placeholder (only contains the {{STOPPED}} sentinel). */
function isStoppedMessage(message: UIMessage): boolean {
  const textParts = message.parts.filter((p) => p.type === 'text') as { text: string }[];
  return textParts.length === 1 && textParts[0].text === '{{STOPPED}}';
}

/** Muted indicator rendered when the user stopped a response mid-stream. */
function StoppedIndicator({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex items-center gap-1.5 py-0.5 text-xs text-muted-foreground">
      <CircleStop className="h-3.5 w-3.5 shrink-0" />
      <span>{t('optibot.stoppedResponse')}</span>
    </div>
  );
}

function groupAndRenderParts(parts: UIMessage['parts'], ctx: RenderContext): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let i = 0;

  while (i < parts.length) {
    const part = parts[i];

    // Text parts
    if (part.type === 'text') {
      if (part.text === '{{STOPPED}}') {
        result.push(<StoppedIndicator key={i} t={ctx.t} />);
      } else if (part.text) {
        result.push(
          <Markdown key={i} remarkPlugins={[remarkGfm]} components={ctx.mdComponents}>
            {part.text}
          </Markdown>,
        );
      }
      i++;
      continue;
    }

    // Tool parts — collect consecutive completed read tools for collapsing
    if (isToolPart(part)) {
      if (isCompletedReadTool(part)) {
        const group: ToolPartShape[] = [part];
        let j = i + 1;
        while (j < parts.length) {
          const next = parts[j];
          if (isToolPart(next) && isCompletedReadTool(next)) {
            group.push(next);
            j++;
          } else {
            break;
          }
        }

        if (group.length >= 2) {
          result.push(<CollapsedToolSteps key={i} steps={group} t={ctx.t} />);
        } else {
          result.push(<ToolInvocationPart key={i} part={part} ctx={ctx} />);
        }
        i = j;
        continue;
      }

      result.push(<ToolInvocationPart key={i} part={part} ctx={ctx} />);
      i++;
      continue;
    }

    i++;
  }

  return result;
}

// --- Collapsed tool steps (R3) ---

function CollapsedToolSteps({
  steps,
  t,
}: {
  steps: ToolPartShape[];
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="py-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className="h-auto gap-1.5 px-0 py-0 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronRight
          className={cn('h-3 w-3 shrink-0 transition-transform', expanded && 'rotate-90')}
        />
        <CheckCircle2 className="h-3 w-3 shrink-0 text-primary" />
        <span>{t('optibot.tool.searchedSources', { count: steps.length })}</span>
      </Button>
      {expanded && (
        <div className="ml-5 mt-1 space-y-0.5">
          {steps.map((step) => (
            <div
              key={step.toolCallId}
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <CheckCircle2 className="h-3 w-3 shrink-0 text-primary" />
              <span>{t(getToolDoneLabel(getToolName(step)))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Individual tool part rendering ---

function ToolInvocationPart({ part, ctx }: { part: ToolPartShape; ctx: RenderContext }) {
  const { t, onApprove, onDeny } = ctx;
  const toolName = getToolName(part);
  const { toolCallId, state } = part;

  // Track the user's decision so the approval card persists as a receipt after state changes.
  const [decision, setDecision] = useState<'approved' | 'denied' | null>(null);

  // Write tool awaiting user approval (AI SDK v6: needsApproval pauses at 'approval-requested')
  if (state === 'approval-requested' && isWriteTool(toolName) && onApprove && onDeny) {
    // AI SDK v6: approval responses must use part.approval.id, not toolCallId
    const approvalId = part.approval?.id ?? toolCallId;
    return (
      <ApprovalCard
        toolCallId={approvalId}
        toolName={toolName}
        args={(part.input as Record<string, unknown>) ?? {}}
        onApprove={(id) => {
          setDecision('approved');
          onApprove(id);
        }}
        onDeny={(id) => {
          setDecision('denied');
          onDeny(id);
        }}
      />
    );
  }

  // Write tool that was actioned — keep the card visible as a receipt.
  // Use state === 'result' OR !ctx.isLoading as the completion signal — AI SDK v6
  // keeps the stream open while the AI generates its response text after the tool
  // executes, so state may not reach 'result' until after isLoading goes false.
  if (decision !== null && isWriteTool(toolName)) {
    const decided =
      decision === 'denied'
        ? 'denied'
        : state === 'result' || !ctx.isLoading
          ? 'approved'
          : 'executing';
    return (
      <ApprovalCard
        toolCallId={toolCallId}
        toolName={toolName}
        args={(part.input as Record<string, unknown>) ?? {}}
        onApprove={() => {}}
        onDeny={() => {}}
        decided={decided}
      />
    );
  }

  // Loading: tool is being called — show 3-dot indicator with label
  // If not loading anymore (user clicked Stop), show as interrupted
  if (state === 'input-streaming' || state === 'input-available') {
    if (!ctx.isLoading) {
      // Interrupted by Stop — show as completed step (no error, just stopped)
      return (
        <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
          <Square className="h-3 w-3 shrink-0" />
          <span>{t(getToolDoneLabel(toolName))}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50"
              style={{
                animation: 'optibot-dot-bounce 1.2s ease-in-out infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </span>
        <span>{t(getToolLabel(toolName))}</span>
      </div>
    );
  }

  // Denied state (AI SDK v6: needsApproval → user clicked Deny)
  if (state === 'output-denied') {
    return (
      <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
        <XCircle className="h-3 w-3 shrink-0" />
        <span>{t('optibot.actionDenied')}</span>
      </div>
    );
  }

  // Error state
  if (state === 'output-error') {
    return (
      <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
        <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
        <span>{part.errorText ?? t('optibot.errorOccurred')}</span>
      </div>
    );
  }

  // Result available
  if (state === 'output-available') {
    const output = part.output;
    const isDenied = output === 'denied';
    const isApproved = output === 'approved';

    if (isWriteTool(toolName) && (isDenied || isApproved)) {
      return (
        <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
          {isDenied ? (
            <>
              <XCircle className="h-3 w-3 shrink-0" />
              <span>{t('optibot.actionDenied')}</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3 w-3 shrink-0 text-primary" />
              <span>{t('optibot.actionCompleted')}</span>
            </>
          )}
        </div>
      );
    }

    // Completed read tool — inline with check icon
    const hasError = isErrorResult(output);

    // Rich card rendering for searchTemplates results
    const hasTemplates = toolName === 'searchTemplates' && isTemplateOutput(output);

    // Rich card rendering for generateDesign results
    const hasDesigns = toolName === 'generateDesign' && isDesignOutput(output);

    return (
      <>
        <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
          {hasError ? (
            <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
          ) : (
            <CheckCircle2 className="h-3 w-3 shrink-0 text-primary" />
          )}
          <span>
            {hasError ? (
              <Markdown remarkPlugins={[remarkGfm]} components={ctx.mdComponents}>
                {(output as { error: string }).error}
              </Markdown>
            ) : (
              t(getToolDoneLabel(toolName))
            )}
          </span>
        </div>
        {hasTemplates && <TemplateCards templates={(output as TemplateToolOutput).templates} />}
        {hasDesigns && <DesignCards designs={(output as DesignToolOutput).designs} />}
      </>
    );
  }

  // Unknown states — show loading dots
  return (
    <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
      <span className="flex items-center gap-0.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50"
            style={{
              animation: 'optibot-dot-bounce 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </span>
      <span>{t(getToolLabel(toolName))}</span>
    </div>
  );
}

// --- Tool output type guards ---

interface TemplateToolOutput {
  templates: Array<{
    templateId: string;
    title: string;
    thumbnailUrl: string;
    dimensions: { width: number; height: number };
    tags: string[];
    score?: string;
  }>;
}

interface DesignToolOutput {
  designs: Array<{
    designId: string;
    title: string;
    orientation: 'portrait' | 'landscape';
    dimensions: { width: number; height: number };
  }>;
}

function isTemplateOutput(output: unknown): output is TemplateToolOutput {
  return (
    typeof output === 'object' &&
    output !== null &&
    'templates' in output &&
    Array.isArray((output as TemplateToolOutput).templates) &&
    (output as TemplateToolOutput).templates.length > 0
  );
}

function isDesignOutput(output: unknown): output is DesignToolOutput {
  return (
    typeof output === 'object' &&
    output !== null &&
    'designs' in output &&
    Array.isArray((output as DesignToolOutput).designs) &&
    (output as DesignToolOutput).designs.length > 0
  );
}

// --- Markdown component overrides ---

/** Factory that creates markdown components with internal link interception */
function createMarkdownComponents(navigate: ReturnType<typeof useNavigate>) {
  return {
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="mb-2 last:mb-0">{children}</p>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="mb-2 ml-4 list-disc last:mb-0">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="mb-2 ml-4 list-decimal last:mb-0">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => <li className="mb-1">{children}</li>,
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      // Internal links — use client-side navigation
      if (href?.startsWith('/app/')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              navigate(href);
            }}
            className="text-primary underline hover:text-primary/80"
          >
            {children}
          </a>
        );
      }
      // External links — open in new tab
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80"
        >
          {children}
        </a>
      );
    },
    code: ({ children }: { children?: React.ReactNode }) => (
      <code className="rounded bg-muted px-1 py-0.5 text-xs">{children}</code>
    ),
    pre: ({ children }: { children?: React.ReactNode }) => (
      <pre className="mb-2 overflow-x-auto rounded bg-muted p-3 text-xs last:mb-0">{children}</pre>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="mb-2 overflow-x-auto last:mb-0">
        <table className="min-w-full text-xs">{children}</table>
      </div>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th className="border-b border-border px-2 py-1 text-left font-medium">{children}</th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td className="border-b border-border px-2 py-1">{children}</td>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <strong className="font-semibold">{children}</strong>
    ),
  };
}
